import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'error' | 'info';

const TONES: Record<BadgeTone, string> = {
  neutral: 'border-border bg-muted text-muted-foreground',
  primary: 'border-primary/25 bg-primary/10 text-primary',
  success: 'border-success/25 bg-success/10 text-success',
  warning: 'border-warning/25 bg-warning/10 text-warning',
  error: 'border-destructive/25 bg-destructive/10 text-destructive',
  info: 'border-info/25 bg-info/10 text-info',
};

export type BadgeProps = {
  tone?: BadgeTone;
  /** Leading dot (states). */
  dot?: boolean;
  /** `sm`: 11 px text, for pills inside dense rows and pickers. */
  size?: 'sm' | 'md';
  /** Uppercase, letter-spaced (short tags such as "même référence"). */
  caps?: boolean;
  className?: string;
  title?: string;
  children: ReactNode;
  'data-testid'?: string;
};

/** Pill for states, counts, tags and highlights; same tones as `Callout`. */
export function Badge({
  tone = 'neutral',
  dot = false,
  size = 'md',
  caps = false,
  className,
  title,
  children,
  'data-testid': testId,
}: BadgeProps) {
  return (
    <span
      title={title}
      data-testid={testId}
      data-tone={tone}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border font-medium',
        size === 'sm' ? 'px-1.5 py-px text-2xs' : 'px-2 py-0.5 text-xs',
        caps && 'font-semibold uppercase tracking-wide',
        TONES[tone],
        className
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}
